import { readRuntimeConfigFlag, readRuntimeConfigValue } from './runtime-config';

const DEV_AUTH_STORAGE_KEY = 'image2video-web-ui:dev-auth:v1';
const DEV_AUTH_COOKIE_NAME = 'image2video_dev_auth';

type DevAuthHeaders = {
  userId: string;
  botId?: string;
};

function isDevTestAccountFeatureEnabled(): boolean {
  return readRuntimeConfigFlag('VITE_ENABLE_TEST_ACCOUNT');
}

function readConfiguredDevAuth(): DevAuthHeaders | null {
  if (!isDevTestAccountFeatureEnabled()) {
    return null;
  }

  const userId = readRuntimeConfigValue('VITE_DEV_USER_ID');
  const botId = readRuntimeConfigValue('VITE_DEV_BOT_ID');

  if (userId === '') {
    return null;
  }

  return {
    userId,
    botId: botId || undefined,
  };
}

export function isDevTestAccountAvailable(): boolean {
  return readConfiguredDevAuth() !== null;
}

export function shouldShowDevTestAccountUi(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { hostname, port } = window.location;
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  const allowedPorts = new Set(['3001', '4173']);

  return isLocalHost && allowedPorts.has(port);
}

export function isDevTestAccountEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!isDevTestAccountFeatureEnabled()) {
    return false;
  }

  return window.localStorage.getItem(DEV_AUTH_STORAGE_KEY) !== null || window.document.cookie.includes(`${DEV_AUTH_COOKIE_NAME}=1`);
}

export function enableConfiguredDevTestAccount(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const configured = readConfiguredDevAuth();
  if (!configured) {
    return false;
  }

  window.localStorage.setItem(DEV_AUTH_STORAGE_KEY, JSON.stringify(configured));
  window.document.cookie = `${DEV_AUTH_COOKIE_NAME}=1; Path=/; SameSite=Lax`;
  return true;
}

export function clearDevTestAccount(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(DEV_AUTH_STORAGE_KEY);
  window.document.cookie = `${DEV_AUTH_COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function readActiveDevAuthHeaders(): DevAuthHeaders | null {
	if (typeof window === 'undefined') {
		return null;
	}

	if (!shouldShowDevTestAccountUi()) {
		clearDevTestAccount();
		return null;
	}

	if (!isDevTestAccountFeatureEnabled()) {
		clearDevTestAccount();
    return null;
  }

  const raw = window.localStorage.getItem(DEV_AUTH_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<DevAuthHeaders>;
    const userId = String(parsed.userId || '').trim();
    const botId = String(parsed.botId || '').trim();

    if (userId === '') {
      clearDevTestAccount();
      return null;
    }

    return {
      userId,
      botId: botId || undefined,
    };
  } catch {
    clearDevTestAccount();
    return null;
  }
}
